/**
 * deriveNavigation — the SINGLE source of truth (server + client) that maps a
 * principal's permission flag-map → the set of allowed left-nav items. Ported
 * from skySign's `shared/rbac/deriveNavigation.ts` (which returns `show*` flags);
 * skyCare's nav is item-id based (see client/shell/nav.ts), so this returns the
 * allowed nav ids instead.
 *
 * Pure + dependency-free so it runs identically in the API (`/api/v1/me`) and in
 * the React shell (nav filtering). Holds NO PHI.
 */

/** Permission flag-map: `{ viewRoster: true, manageUsers: false, ... }`. */
export type PermissionMap = Record<string, boolean>;

/**
 * Sentinel permission that marks a nav id as INTENTIONALLY PUBLIC (reachable by any
 * authenticated principal). The rule is DEFAULT-DENY (fail closed): an unmapped id, an
 * empty permission list, or a null map denies. Only an explicit `[PUBLIC]` entry opens
 * an id to everyone. No nav id is public today; the sentinel exists so that a future
 * public item is a deliberate, greppable declaration and never an accidental fail-open.
 */
export const PUBLIC = "__public__";

/**
 * nav id → the permission(s) that reveal it. A nav item is allowed when the
 * principal holds AT LEAST ONE of the listed permissions (OR semantics, matching
 * skySign's `viewX || manageX` nav gating) — EXCEPT ids in NAV_REQUIRE_ALL, which
 * need EVERY listed permission. An id mapped to `[PUBLIC]` is reachable by anyone.
 * Keep the ids in lock-step with `client/shell/nav.ts` NAV_SECTIONS (the parity test
 * guards this: every NAV_SECTIONS id must have a non-empty entry here).
 */
export const NAV_PERMISSIONS: Readonly<Record<string, ReadonlyArray<string>>> = {
  dashboard: ["viewDashboard"],
  roster: ["viewRoster"],
  vitals: ["viewParticipant"],
  encounters: ["viewParticipant"],
  idt: ["viewParticipant"],
  medications: ["viewParticipant"],
  population: ["viewRoster"],
  census: ["viewCensus"],
  quality: ["viewRoster"],
  "care-gaps": ["viewRoster"],
  utilization: ["viewUtilization"],
  "data-health": ["viewDataHealth"],
  // Administration group (M2 W5b). Each id is revealed by ANY of its listed
  // permissions (OR semantics), except admin-audit-log (see NAV_REQUIRE_ALL).
  "admin-tenants": ["manageTenants"],
  "admin-instances": ["manageInstances"],
  "admin-users": ["viewUsers", "manageUsers"],
  "admin-roles": ["viewRoles", "manageRoles"],
  "admin-audit-log": ["manageUsers", "manageRoles", "manageTenants", "manageInstances"],
};

/**
 * nav ids that require ALL of their NAV_PERMISSIONS (AND semantics), not just one.
 * admin-audit-log mirrors the API's `requireAdmin` guard on GET /admin/audit-log:
 * the read is admin-only, and the seeded `admin` / `skypoint_admin` roles are the only
 * principals that hold the FULL management set. Requiring EVERY manage* flag keeps the
 * sidebar in lock-step with the API — a partial-manage custom role (e.g. manageUsers
 * only) that the API would 403 no longer sees the item — while staying purely
 * permission-driven (no role-name / isAdmin branch in this shared, pure module).
 * OR-gating it (the old behavior) was BROADER than the API guard and revealed a link
 * that then 403s: a false affordance. Fail closed by demanding the whole admin set.
 */
export const NAV_REQUIRE_ALL: ReadonlySet<string> = new Set(["admin-audit-log"]);

/** True when `permissions` grants at least one of `required` (flag === true). */
export function hasAnyPermission(
  permissions: PermissionMap,
  required: ReadonlyArray<string>,
): boolean {
  return required.some((key) => permissions[key] === true);
}

/** True when the nav id is reachable for the given permission map. */
export function canAccessNav(navId: string, permissions: PermissionMap): boolean {
  // DEFAULT-DENY (fail closed): an unmapped id or an empty permission list is NOT
  // reachable — an unknown/misconfigured nav id must never fall through to "visible".
  const required = NAV_PERMISSIONS[navId];
  if (!required || required.length === 0) return false;
  // A null/absent map denies (defensive; the flag-map should always be present).
  if (!permissions) return false;
  // Only an explicit PUBLIC sentinel opens an id to any authenticated principal.
  if (required.includes(PUBLIC)) return true;
  // AND semantics for admin-only ids (mirror the API guard); OR semantics otherwise.
  return NAV_REQUIRE_ALL.has(navId)
    ? required.every((key) => permissions[key] === true)
    : hasAnyPermission(permissions, required);
}

/**
 * Map a permission flag-map → the ordered list of allowed nav ids. The order
 * follows NAV_PERMISSIONS declaration order (the nav-section order). An empty
 * map yields no items; an admin (all-true map) yields every item.
 */
export function deriveNavigation(permissions: PermissionMap): string[] {
  return Object.keys(NAV_PERMISSIONS).filter((navId) => canAccessNav(navId, permissions));
}
